import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, ArrowLeft, ArrowRight, Flag, CheckCircle, Warning } from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function TestTakingPage() {
    const { testId } = useParams();
    const { state } = useLocation();
    const nav = useNavigate();
    const [test, setTest] = useState(state?.test || null);
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({}); // qid -> {selected, time_spent}
    const [flags, setFlags] = useState({}); // qid -> true
    const [remaining, setRemaining] = useState(0);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const questionStart = useRef(Date.now());

    useEffect(() => {
        if (!test && testId) {
            // No prefetched state — go back to courses
            toast.error("Test session lost. Please start a fresh test.");
            nav("/courses");
        }
    }, [test, testId, nav]);

    useEffect(() => {
        if (!test) return;
        setRemaining(test.duration_min * 60);
    }, [test]);

    // countdown
    useEffect(() => {
        if (!test) return;
        const t = setInterval(() => {
            setRemaining(r => {
                if (r <= 1) { clearInterval(t); handleSubmit(true); return 0; }
                return r - 1;
            });
        }, 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test]);

    // reset per-question timer on nav
    useEffect(() => { questionStart.current = Date.now(); }, [idx]);

    const trackTimeAndSet = (qid, patch) => {
        const spent = Math.round((Date.now() - questionStart.current) / 1000);
        setAnswers(prev => ({
            ...prev,
            [qid]: {
                selected_index: patch.selected_index ?? prev[qid]?.selected_index ?? null,
                time_spent_sec: (prev[qid]?.time_spent_sec || 0) + spent,
            },
        }));
        questionStart.current = Date.now();
    };

    const select = (qid, optIdx) => {
        trackTimeAndSet(qid, { selected_index: optIdx });
    };

    const goto = (target) => {
        const q = test.questions[idx];
        // save time to current question
        trackTimeAndSet(q.id, {});
        setIdx(target);
    };

    const handleSubmit = async (auto = false) => {
        if (!test || submitting) return;
        setSubmitting(true);
        const q = test.questions[idx];
        // finalize timer for current question
        const spent = Math.round((Date.now() - questionStart.current) / 1000);
        const finalAnswers = { ...answers };
        finalAnswers[q.id] = {
            selected_index: finalAnswers[q.id]?.selected_index ?? null,
            time_spent_sec: (finalAnswers[q.id]?.time_spent_sec || 0) + spent,
        };
        try {
            const payload = {
                test_id: test.id,
                answers: test.questions.map(qq => ({
                    question_id: qq.id,
                    selected_index: finalAnswers[qq.id]?.selected_index ?? null,
                    time_spent_sec: finalAnswers[qq.id]?.time_spent_sec ?? 0,
                })),
            };
            const { data } = await api.post("/tests/submit", payload);
            if (auto) toast.warning("Time's up — submitted automatically.");
            else toast.success("Test submitted");
            nav(`/results/${data.id}`, { state: { result: data } });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed");
            setSubmitting(false);
        }
    };

    const attempted = useMemo(() =>
        test ? test.questions.filter(q => answers[q.id]?.selected_index != null).length : 0,
        [test, answers]);

    if (!test) return null;
    const q = test.questions[idx];
    const flagged = !!flags[q.id];
    const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
    const ss = String(remaining % 60).padStart(2, "0");
    const lowTime = remaining < 60;

    return (
        <div className="min-h-screen bg-bone" data-testid="test-taking-page">
            {/* Sticky top: timer + progress */}
            <div className="sticky top-0 z-30 bg-bone border-b border-ink/15">
                <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-ink/50">{test.course_name} · {test.test_type === "mock" ? "Full Mock" : "Practice"}</div>
                        <div className="font-serif text-lg text-ink">Question {idx + 1} <span className="text-ink/40">/ {test.questions.length}</span></div>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-lg ${lowTime ? "bg-destructive text-destructive-foreground" : "bg-ink text-bone"}`} data-testid="timer">
                        <Clock size={18}/> {mm}:{ss}
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-6xl px-6 py-10 grid lg:grid-cols-12 gap-10">
                <main className="lg:col-span-8" data-testid="question-panel">
                    <div className="text-[11px] uppercase tracking-widest text-gold mb-2">{q.topic}</div>
                    <h2 className="font-serif text-2xl md:text-3xl text-ink leading-snug">{q.question}</h2>

                    <div className="mt-8 space-y-3">
                        {q.options.map((opt, i) => {
                            const active = answers[q.id]?.selected_index === i;
                            return (
                                <button
                                    key={i}
                                    onClick={() => select(q.id, i)}
                                    data-testid={`option-${i}`}
                                    className={`w-full text-left p-4 border rounded-sm flex items-start gap-3 transition-colors duration-150 ${
                                        active ? "border-ink bg-ink/5" : "border-ink/15 bg-bone hover:border-ink/40"
                                    }`}>
                                    <span className={`w-6 h-6 grid place-items-center rounded-full border text-xs font-medium shrink-0 ${
                                        active ? "bg-ink text-bone border-ink" : "border-ink/30 text-ink/60"
                                    }`}>{String.fromCharCode(65 + i)}</span>
                                    <span className="text-ink">{opt}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-10 flex items-center justify-between">
                        <Button variant="ghost" onClick={() => goto(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="btn-prev" className="text-ink hover:bg-ink/5">
                            <ArrowLeft size={16} className="mr-2"/> Previous
                        </Button>
                        <button onClick={() => setFlags(p => ({ ...p, [q.id]: !p[q.id] }))} data-testid="btn-flag" className={`text-xs flex items-center gap-1.5 px-3 py-1.5 border rounded-sm transition-colors ${flagged ? "border-gold text-gold-dark bg-gold/10" : "border-ink/20 text-ink/60"}`}>
                            <Flag size={14} weight={flagged ? "fill" : "regular"}/> {flagged ? "Marked for review" : "Mark for review"}
                        </button>
                        {idx < test.questions.length - 1 ? (
                            <Button onClick={() => goto(idx + 1)} className="bg-ink text-bone hover:bg-ink/90 rounded-sm" data-testid="btn-next">
                                Next <ArrowRight size={16} className="ml-2"/>
                            </Button>
                        ) : (
                            <Button onClick={() => setConfirmSubmit(true)} className="bg-gold text-ink hover:bg-gold/90 rounded-sm" data-testid="btn-open-submit">
                                Review & submit
                            </Button>
                        )}
                    </div>
                </main>

                <aside className="lg:col-span-4">
                    <div className="border border-ink/15 rounded-sm bg-bone p-6 sticky top-24" data-testid="navigator-panel">
                        <div className="text-xs uppercase tracking-widest text-ink/50">Navigator</div>
                        <div className="text-sm text-ink/70 mt-1">Attempted <span className="font-serif text-ink text-lg">{attempted}</span> / {test.questions.length}</div>
                        <div className="grid grid-cols-6 gap-2 mt-5">
                            {test.questions.map((qq, i) => {
                                const a = answers[qq.id]?.selected_index != null;
                                const f = flags[qq.id];
                                return (
                                    <button key={qq.id} onClick={() => goto(i)} data-testid={`nav-q-${i}`}
                                        className={`h-9 text-xs rounded-sm border transition-colors duration-150 ${
                                            i === idx ? "border-ink bg-ink text-bone" :
                                            f ? "border-gold bg-gold/10 text-gold-dark" :
                                            a ? "border-ink/60 bg-ink/10 text-ink" :
                                            "border-ink/15 bg-bone text-ink/60 hover:border-ink/40"
                                        }`}>{i + 1}</button>
                                );
                            })}
                        </div>
                        <div className="mt-6 space-y-1.5 text-[11px] text-ink/60">
                            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-ink rounded-sm"></span> Current</div>
                            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-ink/10 border border-ink/60 rounded-sm"></span> Answered</div>
                            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-gold/10 border border-gold rounded-sm"></span> Marked</div>
                            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-bone border border-ink/15 rounded-sm"></span> Unseen</div>
                        </div>
                        <Button onClick={() => setConfirmSubmit(true)} className="w-full mt-6 bg-ink text-bone hover:bg-ink/90 rounded-sm" data-testid="btn-submit-side">
                            Submit test
                        </Button>
                    </div>
                </aside>
            </div>

            <Dialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
                <DialogContent data-testid="submit-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Submit your test?</DialogTitle>
                        <DialogDescription>
                            You've attempted <b>{attempted}</b> of <b>{test.questions.length}</b> questions.
                            {attempted < test.questions.length && (
                                <span className="mt-2 flex items-center gap-2 text-oxblood"><Warning size={14}/> {test.questions.length - attempted} unattempted</span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmSubmit(false)} data-testid="btn-cancel-submit">Keep going</Button>
                        <Button onClick={() => handleSubmit(false)} disabled={submitting} className="bg-ink text-bone hover:bg-ink/90" data-testid="btn-confirm-submit">
                            {submitting ? "Submitting…" : <>Submit <CheckCircle size={16} className="ml-2"/></>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
